import _ from 'lodash'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { IconNames } from '@blueprintjs/icons'
import { Try } from 'shared-libs/helpers/utils'
import { Button, Icon } from '@blueprintjs/core'
import CodeMirror, { ReactCodeMirrorProps, ReactCodeMirrorRef } from '@uiw/react-codemirror'
import { solarizedLight } from '@uiw/codemirror-theme-solarized'
import { Diagnostic, forEachDiagnostic, linter } from '@codemirror/lint'
import { ViewUpdate, showPanel, EditorView } from '@codemirror/view'
import { Transaction, Text, Extension } from '@codemirror/state'
import { json, jsonParseLinter } from '@codemirror/lang-json'
import { javascript, javascriptLanguage, typescriptLanguage } from '@codemirror/lang-javascript'

import 'browser/components/atomic-elements/atoms/input/_code-mirror-input.scss'

class StatusLine extends React.Component {
  private statusLine: string = undefined

  public setStatusLine(statusLine: string) {
    this.statusLine = statusLine
    this.forceUpdate()
  }

  public render() {
    return <div className="code tr f6 pa1 bg-black-05">{this.statusLine}</div>
  }
}

interface ICodeMirrorInputProps extends ReactCodeMirrorProps {
  showStatusLine?: boolean
  language?: string
}

export class CodeMirrorInput extends React.Component<ICodeMirrorInputProps> {
  static defaultProps: Partial<ICodeMirrorInputProps> = {
    showStatusLine: true,
  }

  private editorRef = React.createRef<ReactCodeMirrorRef>()
  private statusLineRef = React.createRef<StatusLine>()

  constructor(props: ICodeMirrorInputProps) {
    super(props)
    this.state = {}
  }

  shouldComponentUpdate(nextProps: Readonly<ICodeMirrorInputProps>): boolean {
    return this.props.value !== nextProps.value
  }

  public render() {
    return (
      <div className="c-section h-100 flex flex-column flex-grow">
        {this.renderEditor()}
        {this.renderStatusLine()}
      </div>
    )
  }

  private renderEditor() {
    const { value, language } = this.props
    const { showStatusLine, ...props } = this.props
    const extensions = getLanguageExtensions(language)
    return (
      <CodeMirror
        className="overflow-y-auto"
        theme={solarizedLight}
        style={{
          "flexGrow": 1,
        }}
        basicSetup={{
          foldGutter: true,
          foldKeymap: true,
          lineNumbers: true,
          highlightActiveLineGutter: true,
          highlightActiveLine: false,
        }}
        extensions={extensions}
        {...props}
        ref={this.editorRef}
        onChange={this.handleOnChange}
        onUpdate={(viewUpdate) => {
          if (showStatusLine) {
            // Set the computed status line text directly on the ref instead of
            // setting state here, due to a flaw in the React CodeMirror
            // wrapper. It seems to not behave nicely during a re-render.
            //
            // For example:
            //
            //  * CMD+F
            //  * type search text
            //  * press Enter or hit Next
            //
            // This triggers `onUpdate` with the new text selection state, and
            // if we set state here then CM seems to unexpectedly destroy its
            // search dialog during the re-render.
            this.statusLineRef.current.setStatusLine(this.getStatusLineText(viewUpdate))
          }
        }}
        value={value}
      />
    )
  }

  private handleOnChange = (value: string, update: ViewUpdate) => {
    const { onChange } = this.props
    onChange?.(value, update)
  }

  private renderStatusLine() {
    const { showStatusLine } = this.props
    if (!showStatusLine) {
      return null
    }
    return <StatusLine ref={this.statusLineRef} />
  }

  private getStatusLineText(viewUpdate: ViewUpdate) {
    const anchor = this.getStatusLine(viewUpdate.state.doc, viewUpdate.state.selection.main.anchor)
    const head = this.getStatusLine(viewUpdate.state.doc, viewUpdate.state.selection.main.head)

    const line = this.formatRange(anchor.line, head.line)
    const col = this.formatRange(anchor.column, head.column)
    const offset = this.formatRange(anchor.offset, head.offset)
    return `Ln ${line}, Col ${col}, Pos ${offset}`
  }

  /* display two numbers as a range if they are unique */
  private formatRange: (from: number, to: number) => string = _.flow([
    _.concat,
    _.uniq,
    _.sortBy,
    _.partial(_.join, _, ' - '),
  ])

  private getStatusLine(doc: Text, pos: number) {
    const line = doc.lineAt(pos)
    return { line: line.number, column: pos - line.from, offset: pos }
  }

  public getEditor() {
    return this.editorRef.current
  }

  public setText(text, opts: { isRemote?: boolean } = {}) {
    const { isRemote = false } = opts
    this.editorRef.current.view.dispatch({
      changes: {
        from: 0,
        to: this.editorRef.current.view.state.doc.length,
        insert: text,
      },
      annotations: [Transaction.remote.of(isRemote)],
    })
  }

  public hasError() {
    let hasError = false
    forEachDiagnostic(this.editorRef.current.state, (diag, from, to) => {
      if (diag.severity === 'error') {
        hasError = true
      }
    })
    return hasError
  }
}

/** Position of the first Lezer error node, or null if the source parses cleanly. */
function firstSyntaxError(parser: any, src: string): { from: number; to: number } | null {
  let error: { from: number; to: number } | null = null
  parser.parse(src).iterate({
    enter: (node: any) => {
      if (!error && node.type.isError) {
        error = { from: node.from, to: node.to }
      }
    },
  })
  return error
}

/*
 * Lightweight syntax sanity check for JS/TS fields: parse with the (already-loaded, for highlighting)
 * Lezer grammar and flag the first error node. Cheaper than compiling the expression — no codegen or
 * Function construction — and good enough to catch malformed code (unbalanced parens, stray keywords).
 * It is syntax-only: it will not catch unknown variables or type errors. Uses the parser matching the
 * field's language so valid TS syntax isn't flagged in a `typescript` field.
 *
 * `{{...}}` template tokens (ingestion munging expressions) aren't valid JS, so mask each with an
 * equal-length identifier before parsing; equal length keeps every following offset — and thus the error
 * position — aligned with the original text. A non-greedy match tolerates inner/adjacent braces.
 */
function makeSyntaxLinter(parser: any): Extension {
  return linter((view): Diagnostic[] => {
    const doc = view.state.doc.toString()
    if (doc.trim() === '') {
      return []
    }
    const masked = doc.replace(/\{\{[\s\S]*?\}\}/g, (token) => '_'.repeat(token.length))
    let error = firstSyntaxError(parser, masked)
    // A munging expression may be a bare object literal — the runtime treats a leading `{` as the
    // returned object. At statement position that parses as a block and errors, so re-check it as a
    // parenthesized expression before flagging.
    if (error && masked.trim().startsWith('{') && !firstSyntaxError(parser, `(${masked})`)) {
      error = null
    }
    if (!error) {
      return []
    }
    const from = Math.min(error.from, Math.max(0, doc.length - 1))
    const to = Math.min(Math.max(error.to, from + 1), doc.length)
    return [{ from, to, severity: 'error', message: 'Syntax error' }]
  })
}

const jsSyntaxLinter = makeSyntaxLinter(javascriptLanguage.parser)
const tsSyntaxLinter = makeSyntaxLinter(typescriptLanguage.parser)

function getLanguageExtensions(language?: string): Extension[] {
  switch (language) {
    case 'json':
      return [json(), linter(jsonParseLinter())]
    case 'javascript':
      return [javascript(), jsSyntaxLinter]
    case 'typescript':
      return [javascript({ typescript: true }), tsSyntaxLinter]
    default:
      return []
  }
}

export function formatButtonPanelExts(formatter: (string) => string): Extension[] {
  return [
    showPanel.of((view: EditorView) => {
      const dom = document.createElement('div')
      dom.className = 'relative'
      const root = createRoot(dom)
      root.render(
        <div className="absolute top-0 right-0 pa1">
          <Button onClick={() => handleFormatClick(view, formatter)}>
            <Icon icon={IconNames.CLEAN} />
          </Button>
        </div>
      )
      return { top: true, dom, destroy: () => root.unmount() }
    }),

    EditorView.baseTheme({
      // override panel z-index so CM lines don't prevent button clicks
      '.cm-panels': {
        zIndex: 1,
      },
    }),
  ]
}

function handleFormatClick(view: EditorView, formatter: (string) => string) {
  const formatted = Try(() => formatter(view.state.doc.toString()))
  if (formatted) {
    view.dispatch({
      changes: {
        from: 0,
        to: view.state.doc.length,
        insert: formatted,
      },
    })
  }
}
